import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Modal,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { getRCSTemplatesALL, sendAnnouncementRCS } from "../../Services";

/** -------------------------------------------
 *  Helpers
 *  -------------------------------------------
 */

// Replace tokens like {{0}}, {{1}} and also %name%, %email% etc.
function applyVariables(str = "", variablesMap = {}) {
  if (!str) return "";

  // {{0}} style
  let out = str.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
    variablesMap?.[key] ?? `{{${key}}}`
  );

  // %name% style
  out = out.replace(/%(\w+?)%/g, (_, key) => variablesMap?.[key] ?? `%${key}%`);

  return out;
}

// For review summary formatting
const currency = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });

/** -------------------------------------------
 *  Preview components (minimal visual parity)
 *  -------------------------------------------
 */

const DarkPhoneShell = ({ children }) => (
  <div
    className="rounded-3"
    style={{
      background: "#111",
      borderRadius: 16,
      padding: 16,
      width: 320,
      color: "#f8f8f8",
    }}
  >
    {children}
  </div>
);

export function RichCardPreview({ card }) {
  if (!card) return null;
  return (
    <DarkPhoneShell>
      {card.mediaUrl && !card.mediaUrl.includes("mp4") && (
        <div
          className="mb-2"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            height: 160,
            background: "#222",
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img
            src={card.mediaUrl}
            alt="Header Image"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
      )}
      {card.mediaUrl && card.mediaUrl.includes("mp4") && (
        <video
          src={card.mediaUrl}
          controls
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      <div className="mb-1" style={{ fontWeight: 700, fontSize: 14 }}>
        {card.cardTitle}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "#d9d9d9" }}>
        {card.cardDescription}
      </p>

      {Array.isArray(card.suggestions) && card.suggestions.length > 0 && (
        <>
          <Divider className="my-2" style={{ borderColor: "#333" }} />
          <div className="d-flex gap-2">
            {card.suggestions.slice(0, 3).map((sug, i) => (
              <button
                key={i}
                className="btn btn-outline-light"
                style={{
                  borderRadius: 20,
                  padding: "4px 10px",
                  borderColor: "#666",
                  fontSize: 12,
                }}
              >
                {sug.text || sug.suggestionType}
              </button>
            ))}
          </div>
        </>
      )}
    </DarkPhoneShell>
  );
}

export function CarouselPreview({ carouselCard }) {
  if (!Array.isArray(carouselCard) || !carouselCard.length) return null;
  return (
    <DarkPhoneShell>
      <div
        className="d-flex"
        style={{ gap: 12, overflowX: "auto", paddingBottom: 4 }}
      >
        {carouselCard.map((c, idx) => (
          <div
            key={idx}
            className="p-2"
            style={{
              width: 240,
              minWidth: 240,
              borderRadius: 12,
              background: "#1a1a1a",
            }}
          >
            {c.mediaUrl && !c.mediaUrl.includes("mp4") && (
              <div
                style={{
                  height: 120,
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 8,
                  background: "#222",
                }}
              >
                <img
                  src={c.mediaUrl}
                  alt="Card"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            )}
            {c.mediaUrl && c.mediaUrl.includes("mp4") && (
              <video
                src={c.mediaUrl}
                controls
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            <div style={{ fontWeight: 700, fontSize: 13 }}>{c.cardTitle}</div>
            <div style={{ fontSize: 12, color: "#d9d9d9" }}>
              {c.cardDescription}
            </div>
          </div>
        ))}
      </div>
    </DarkPhoneShell>
  );
}

export function TextTemplatePreview({ textMessage, suggestions }) {
  return (
    <DarkPhoneShell>
      <p style={{ margin: 0, fontSize: 14, whiteSpace: "pre-wrap" }}>
        {textMessage}
      </p>
      {Array.isArray(suggestions) && suggestions.length > 0 && (
        <>
          <Divider className="my-2" style={{ borderColor: "#333" }} />
          <div className="d-flex justify-content-center gap-2">
            {suggestions.slice(0, 3).map((sug, i) => (
              <button
                key={i}
                className="btn btn-outline-light"
                style={{
                  borderRadius: 20,
                  padding: "4px 10px",
                  borderColor: "#666",
                  fontSize: 12,
                }}
              >
                {sug.text || sug.suggestionType}
              </button>
            ))}
          </div>
        </>
      )}
    </DarkPhoneShell>
  );
}

/** -------------------------------------------
 *  Main modal
 *  -------------------------------------------
 */

export default function NewRcsCampaignModal({
  open,
  onClose,
  onSuccess,
}) {
  // Redux state
  const authtoken = useSelector((state) => state.main.auth_token);
  const localdata = useSelector((state) => state.main.localdata);

  // Step control: 0 = Details, 1 = Choose Template, 2 = Fill Variables, 3 = Review, 4 = Success
  const [step, setStep] = useState(0);

  // Step 1: details
  const [campaignName, setCampaignName] = useState("");
  const [label, setLabel] = useState("");
  const [labelsList, setLabelsList] = useState([
    // TODO: populate from your labels API if needed
    "New customers",
    "Warm leads",
    "All users",
  ]);

  // Step 2: templates
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]); // API list: expect [{ templatePlaceHolderCount, viTemplate: {...} }, ...]
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // viTemplate

  // Step 3: variables
  const [varMap, setVarMap] = useState({}); // { name: 'Sneh', 0: 'value', ... }
  const [fallbackEnabled, setFallbackEnabled] = useState(false);

  // Review deriveds (mocked costs — replace with your real math)
  const audienceSize = 4500;
  const costPerMsg = 1; // ₹1 demo
  const requiredBal = audienceSize * costPerMsg;
  const currentBal = 10500;

  /** -------------------------------------------
   *  API
   *  -------------------------------------------
   */
  const getAllRCSTemplatesAPICall = () => {
    const token = authtoken;
    const data = {
      senderId: localdata?.rcsUserDetails?.senderId,
    };

    getRCSTemplatesALL(token, data).then((res) => {
      if (res?.flag) {
        setTemplates(Array.isArray(res?.data?.templateInfo) ? res.data.templateInfo : []);
      } else {
        console.log("error occurred");
      }
    });
  };

  const sendAnnouncementRCSdata = () => {
    const token = authtoken;
    // if variableKeys are empty or '', do not pass 
    const templateParameters = (variableKeys || []).map((k) => `${varMap?.[k] ?? ""}`);
    console.log("templateParameters", templateParameters);
    const filteredTemplateParameters = templateParameters.map((p) => p.trim()).filter((p) => p !== "");
    console.log("filteredTemplateParameters", filteredTemplateParameters);
    if (filteredTemplateParameters.length === 0) {
      filteredTemplateParameters = [];
    }


    const data = {
      campaignName: campaignName,
      templateName: selected?.name,
      templateId: selected?._id || selected?.id || "",
      senderId: localdata?.rcsUserDetails?.senderId,
      botId: localdata?.rcsUserDetails?.botId,
      esmeAddr: localdata?.rcsUserDetails?.esmeAddr,
      labelName: label,
      Language_code: "en",
      contactArr: [],
      templateParameters: filteredTemplateParameters,
      // cta: { ... } 
    };

    console.log("data", data);

    // sendAnnouncementRCS(token, data).then((res) => {
    //   if (res?.flag) {
    //     console.log("success");
    //   } else {
    //     console.log("fail");
    //   }
    // });

    return data; // useful for debugging / tests
  };

  useEffect(() => {
    if (open && step === 1 && templates.length === 0) {
      (async () => {
        try {
          setLoadingTemplates(true);
          getAllRCSTemplatesAPICall();
        } finally {
          setLoadingTemplates(false);
        }
      })();
    }
  }, [open, step, templates.length]);

  const variableKeys = useMemo(() => {
    if (!selected) return [];
    const raw = selected.variables || [];
    const unique = [...new Set(raw.filter((v) => v !== "" && v != null))];
    return unique.length ? unique : ["name"]; // fallback to %name% for UX
  }, [selected]);

  // Compute preview object with variables applied
  const preview = useMemo(() => {
    if (!selected) return null;
    const t = selected;
    if (t.type === "richcard") {
      const card = {
        ...t.standaloneCard,
        cardTitle: applyVariables(t?.standaloneCard?.cardTitle, varMap),
        cardDescription: applyVariables(t?.standaloneCard?.cardDescription, varMap),
      };
      return { type: "richcard", card };
    }
    if (t.type === "carousel") {
      const carouselCard = (t.carouselCard || []).map((c) => ({
        ...c,
        cardTitle: applyVariables(c.cardTitle, varMap),
        cardDescription: applyVariables(c.cardDescription, varMap),
      }));
      return { type: "carousel", carouselCard };
    }
    if (t.type === "text") {
      return {
        type: "text",
        textMessage: applyVariables(t.textMessage, varMap),
        suggestions: t.suggestions || [],
      };
    }
    return null;
  }, [selected, varMap]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(0);
        setCampaignName("");
        setLabel("");
        setTemplates([]);
        setSelected(null);
        setVarMap({});
        setFallbackEnabled(false);
      }, 250);
    }
  }, [open]);

  const canNextFromDetails = campaignName.trim() && label.trim();
  const canNextFromTemplate = !!selected;
  const canSend = !!selected && canNextFromDetails;

  const handleRun = async () => {
    const data = sendAnnouncementRCSdata();

    // const res = await sendAnnouncementRCS(authtoken, data);
    // if (res?.flag) {
    //   onSuccess?.(res);
    //   setStep(4); // success screen
    // } else {
    //   alert(res?.message || "Failed to send");
    // }

    // setStep(4);
  };

  // Layout
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        className="bg-white"
        style={{
          width: 920,
          maxWidth: "96vw",
          maxHeight: "92vh",
          overflow: "hidden",
          borderRadius: 16,
          margin: "24px auto",
          padding: 0,
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: 16, borderBottom: "1px solid #eee" }}
        >
          <div>
            <div className="fw-bold" style={{ fontSize: 18 }}>
              {step === 0 && "New RCS campaign"}
              {step === 1 && "Choose campaign template"}
              {step === 2 && "Add template data"}
              {step === 3 && "Review Campaign"}
              {step === 4 && "Campaign Initiated"}
            </div>
          </div>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Content */}
        <div
          style={{
            padding: 16,
            overflowY: "auto",
            maxHeight: "calc(92vh - 120px)",
          }}
        >
          {/* STEP 0: Basic details */}
          {step === 0 && (
            <div>
              <label className="fw-bold d-block mb-1">
                Name Campaign <span style={{ opacity: 0.5 }}>ⓘ</span>
              </label>
              <TextField
                fullWidth
                size="small"
                placeholder="Name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />

              <div className="mt-3">
                <label className="fw-bold d-block mb-1">
                  Select contact label for campaign
                </label>
                <Select
                  fullWidth
                  size="small"
                  value={label}
                  displayEmpty
                  onChange={(e) => setLabel(e.target.value)}
                  renderValue={(v) =>
                    v ? v : <span style={{ color: "#9e9e9e" }}>Select label</span>
                  }
                >
                  {labelsList.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <Button
                variant="outlined"
                className="mt-3"
                onClick={() => alert("Upload label flow (plug yours)")}
              >
                Upload New Label
              </Button>
            </div>
          )}

          {/* STEP 1: Choose template */}
          {step === 1 && (
            <div>
              <TextField
                placeholder="Search for template"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <div className="mt-3" />

              {loadingTemplates ? (
                <div className="d-flex justify-content-center py-5">
                  <CircularProgress />
                </div>
              ) : (
                <Grid container spacing={2}>
                  {(templates || [])
                    .filter((t) => {
                      const name = t?.viTemplate?.name || "";
                      return name.toLowerCase().includes(search.toLowerCase());
                    })
                    .map((t, idx) => {
                      const vt = t.viTemplate || {};
                      const isSelected = selected?.name === vt.name;
                      return (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Card
                            variant="outlined"
                            className={isSelected ? "border-primary" : ""}
                            style={{
                              borderRadius: 14,
                              borderColor: isSelected ? "#7C4DFF" : "#eee",
                            }}
                          >
                            <CardActionArea onClick={() => setSelected(vt)}>
                              <CardContent>
                                <Typography
                                  fontWeight={700}
                                  fontSize={14}
                                  className="mb-2"
                                >
                                  {vt?.type === "text"
                                    ? "Text template"
                                    : vt?.type === "richcard"
                                      ? "Case study template"
                                      : "Marketing template"}
                                </Typography>

                                {/* Compact preview card */}
                                <div className="d-flex justify-content-center">
                                  {vt.type === "richcard" && (
                                    <RichCardPreview card={vt.standaloneCard} />
                                  )}
                                  {vt.type === "carousel" && (
                                    <CarouselPreview
                                      carouselCard={vt.carouselCard}
                                    />
                                  )}
                                  {vt.type === "text" && (
                                    <TextTemplatePreview
                                      textMessage={vt.textMessage}
                                      suggestions={vt.suggestions}
                                    />
                                  )}
                                </div>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>
              )}
            </div>
          )}

          {/* STEP 2: Add template data (variables + preview) */}
          {step === 2 && selected && (
            <div className="row">
              <div className="col-7">
                {/* Header / Media (read-only for announcement flow) */}
                {selected.type !== "text" && (
                  <>
                    <div className="fw-bold mb-2">Header</div>
                    {selected.type === "richcard" && (
                      <div className="mb-3">
                        <img
                          src={selected?.standaloneCard?.mediaUrl}
                          alt="header"
                          style={{
                            width: "100%",
                            height: 180,
                            objectFit: "cover",
                            borderRadius: 12,
                          }}
                        />
                      </div>
                    )}
                    {selected.type === "carousel" && (
                      <div className="d-flex gap-2 mb-3" style={{ overflowX: "auto" }}>
                        {(selected.carouselCard || []).map((c, i) => (
                          <img
                            key={i}
                            src={c.mediaUrl}
                            alt={"c" + i}
                            style={{
                              width: 180,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 12,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Variables */}
                {variableKeys.length > 0 && (
                  <>
                    {variableKeys.map((key) => (
                      <div className="mb-2" key={key}>
                        <label className="fw-bold d-block mb-1">{`{{${key}}}`}</label>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={`Enter value for {{${key}}}`}
                          value={varMap[key] ?? ""}
                          onChange={(e) =>
                            setVarMap((m) => ({ ...m, [key]: e.target.value }))
                          }
                        />
                      </div>
                    ))}
                  </>
                )}

                {/* Fallback */}
                <div className="mt-3">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fallbackEnabled}
                        onChange={(e) => setFallbackEnabled(e.target.checked)}
                      />
                    }
                    label="Configure fall back response"
                  />
                </div>
              </div>

              {/* Live preview */}
              <div className="col-5">
                <div className="fw-bold mb-2">Message Preview</div>
                {preview?.type === "richcard" && (
                  <RichCardPreview card={preview.card} />
                )}
                {preview?.type === "carousel" && (
                  <CarouselPreview carouselCard={preview.carouselCard} />
                )}
                {preview?.type === "text" && (
                  <TextTemplatePreview
                    textMessage={preview.textMessage}
                    suggestions={preview.suggestions}
                  />
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Review */}
          {step === 3 && selected && (
            <div className="row">
              <div className="col-7">
                <div className="fw-bold mb-2">Campaign Summary</div>
                <div className="mb-2">
                  <div className="text-muted">Campaign Name</div>
                  <div>{campaignName}</div>
                </div>
                <div className="mb-2">
                  <div className="text-muted">Template Name</div>
                  <div>{selected?.name}</div>
                </div>
                <div className="mb-2">
                  <div className="text-muted">Send to label</div>
                  <div>{label}</div>
                </div>
                <div className="mb-2">
                  <div className="text-muted">Audience size</div>
                  <div>{audienceSize}</div>
                </div>
                <div className="mb-2">
                  <div className="text-muted">Required Balance</div>
                  <div>
                    {currency(requiredBal)}{" "}
                    {currentBal >= requiredBal ? "✓" : "✗"}
                  </div>
                </div>
                <div className="mb-2">
                  <div className="text-muted">Current Balance</div>
                  <div>{currency(currentBal)}</div>
                </div>
                <div className="mb-2">
                  <div className="text-muted">Fall back response</div>
                  <div>{fallbackEnabled ? "Configured" : "Not Configured"}</div>
                </div>
              </div>

              <div className="col-5">
                <div className="fw-bold mb-2">Message Preview</div>
                {preview?.type === "richcard" && (
                  <RichCardPreview card={preview.card} />
                )}
                {preview?.type === "carousel" && (
                  <CarouselPreview carouselCard={preview.carouselCard} />
                )}
                {preview?.type === "text" && (
                  <TextTemplatePreview
                    textMessage={preview.textMessage}
                    suggestions={preview.suggestions}
                  />
                )}
              </div>
            </div>
          )}

          {/* STEP 4: Success */}
          {step === 4 && (
            <div className="text-center py-5">
              <div
                className="mx-auto mb-3"
                style={{
                  width: 84,
                  height: 84,
                  background: "#E8F5E9",
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: "#2e7d32",
                  fontSize: 48,
                }}
              >
                ✓
              </div>
              <div className="fw-bold" style={{ fontSize: 20 }}>
                Campaign Initiated
              </div>
              <p className="text-muted mb-0">
                Campaign will execute for a while. Please check the progress
                later.
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ padding: 16, borderTop: "1px solid #eee" }}
        >
          <div className="d-flex gap-2">
            {step > 0 && step < 4 && (
              <Button variant="text" onClick={() => setStep((s) => s - 1)}>
                Back
              </Button>
            )}
          </div>

          <div className="d-flex gap-2">
            {step < 3 && (
              <Button
                variant="contained"
                disabled={
                  (step === 0 && !canNextFromDetails) ||
                  (step === 1 && !canNextFromTemplate)
                }
                onClick={() => setStep((s) => s + 1)}
                style={{ background: "#7C4DFF" }}
              >
                {step === 2 ? "Next" : "Save & Next"}
              </Button>
            )}

            {step === 3 && (
              <Button
                variant="contained"
                onClick={handleRun}
                disabled={!canSend || currentBal < requiredBal}
                style={{ background: "#7C4DFF" }}
              >
                Run Campaign
              </Button>
            )}

            {step === 4 && (
              <>
                <Button
                  variant="contained"
                  onClick={() => onSuccess?.()}
                  style={{ background: "#7C4DFF" }}
                >
                  Go to Analytics
                </Button>
                <Button variant="text" onClick={onClose}>
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
}
